import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import personImage from "../images/person.JPG"

// --- About Page Component ---
const AboutPage = ({ theme }) => {
  // Receive theme prop
  const educationRef = useRef(null);
  const experienceRef = useRef(null);
  const projectsRef = useRef(null); // Ref for projects section
  const textRef = useRef(null); // Ref for the bio text
  const [summarizedBio, setSummarizedBio] = useState("");
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [summaryError, setSummaryError] = useState("");

  const { scrollYProgress: educationScrollYProgress } = useScroll({
    target: educationRef,
    offset: ["start 0.9", "end 0.1"],
  });
  const { scrollYProgress: experienceScrollYProgress } = useScroll({
    target: experienceRef,
    offset: ["start 0.9", "end 0.1"],
  });
  const { scrollYProgress: projectsScrollYProgress } = useScroll({
    // Scroll progress for projects
    target: projectsRef,
    offset: ["start 0.9", "end 0.1"],
  });
  const { scrollYProgress: textScrollYProgress } = useScroll({
    target: textRef,
    offset: ["start 0.9", "end 0.1"],
  });

  const educationOpacity = useTransform(
    educationScrollYProgress,
    [0, 0.5, 1],
    [0, 1, 1]
  );
  const educationY = useTransform(
    educationScrollYProgress,
    [0, 0.5, 1],
    [50, 0, 0]
  );

  const experienceOpacity = useTransform(
    experienceScrollYProgress,
    [0, 0.5, 1],
    [0, 1, 1]
  );
  const experienceY = useTransform(
    experienceScrollYProgress,
    [0, 0.5, 1],
    [50, 0, 0]
  );

  const projectsOpacity = useTransform(
    projectsScrollYProgress,
    [0, 0.5, 1],
    [0, 1, 1]
  ); // Opacity for projects
  const projectsY = useTransform(
    projectsScrollYProgress,
    [0, 0.5, 1],
    [50, 0, 0]
  ); // Y-transform for projects

  const textOpacity = useTransform(textScrollYProgress, [0, 0.5, 1], [0, 1, 1]);
  const textY = useTransform(textScrollYProgress, [0, 0.5, 1], [30, 0, 0]);

  const educationData = [
    {
      year: "January 2024 - January 2026",
      title: "MCA",
      institution: "Amity University",
      description: "Pursuing Master of Computer Applications.",
    },
    {
      year: "July 2019 - July 2022",
      title: "BCA in Computer Application",
      institution: "Chhatrapati Shahu Ji Maharaj University, Kanpur",
      description:
        "Major Project: College Management Website using HTML, CSS, JavaScript, PHP, MySQL. Built responsive user interfaces and implemented server-side logic with MySQL database connections through XAMPP.",
    },
  ];

  const experienceData = [
    {
      year: "May 2025 - Present",
      title: "Full Stack Developer (Freelance)",
      company: "Fiverr",
      description:
        "Actively working on various client projects, delivering full-stack web solutions from concept to deployment. Specializing in responsive front-end development with React/Next.js and robust back-end APIs using Node.js/Flask, ensuring high-quality and scalable applications.",
    },
    {
      year: "January 2023 - May 2024",
      title: "Associate",
      company: "Carelon Global Solutions",
      description:
        "Coordinated administrative tasks across departments to ensure smooth patient operations. Maintained accurate documentation and managed confidential health records with strict adherence to protocols. Collaborated with tech vendors during EMR implementation, gaining early exposure to digital systems.",
    },
    {
      year: "July 2022 - January 2023",
      title: "Software Developer, Internship",
      company: "Codemunch (Internship)", // Company name not specified in resume for internship
      description:
        "Developed responsive web applications using ReactJS by creating reusable components and integrating them with backend APIs. Worked on deployment pipelines using GitHub Actions and Azure DevOps (basic CI/CD).",
    },
  ];

  const projectsData = [
    {
      title: "AI Disaster Prediction App",
      web: "",
      technologies: "Shadcn, Next.js, JavaScript, Mapbox",
      description:
        "This project is a web-based application designed to visualize potential flood risk zones using a dynamic map. The app leverages the Mapbox GL JS library to create a powerful and interactive map interface. This project showcases my ability to work with geospatial APIs, integrate front-end libraries like Mapbox GL JS, and build responsive, data-driven web applications using HTML, CSS, and JavaScript.",
    },
    {
      title: "Codemunch App",
      web: "https://codemunch.vercel.app/",
      technologies: "React.js, JavaScript, MongoDB",
      description:
        "CodeMunch is a developer-centric website built with React.js and deployed on Vercel. It features a sleek dark-themed UI, smooth navigation, and responsive design, showcasing frontend skills and clean component-based architecture.",
    },
    {
      title: "Medical Chat Bot",
      web: "https://medicalassistant-zeta.vercel.app/",
      technologies: "React.js, Google API, JavaScript",
      description:
        "Developed a responsive Medical Assistant Chatbot using React.js, JavaScript, and Google API to deliver real-time, AI-powered responses to health-related queries. Designed with a clean UI and smooth user experience for quick, informative interactions.",
    },
    {
      title: "College Management Website",
      web: "https://narainacollegeproject.netlify.app/",
      technologies: "HTML, CSS, JavaScript, PHP, MySQL",
      description:
        "Front-End Development: Built responsive user interfaces using HTML, CSS, and JavaScript for optimal user experience across devices. Backend Development: Implemented server-side logic using PHP and established MySQL database connections through XAMPP.",
    },
    {
      title: "Animated Contact Form",
      web: "https://my-app-gules-ten-56.vercel.app/",
      technologies: "Next.js, ReactBits, JavaScript, EmailJS",
      description:
        "Designed and developed a fully animated Contact Form using React.js and JavaScript. Features smooth transitions, interactive UI elements, and responsive design to enhance user engagement and provide a modern, user-friendly experience.",
    },
  ];

  const handleSummarizeBio = async () => {
    setIsSummarizing(true);
    setSummaryError("");
    setSummarizedBio(""); // Clear previous summary

    const bioText = textRef.current ? textRef.current.innerText : "";
    if (!bioText) {
      setSummaryError("No bio text found to summarize.");
      setIsSummarizing(false);
      return;
    }

    const prompt = `Summarize the following personal bio in 2-3 concise sentences, highlighting key skills and experience:\n\n"${bioText}"`;

    try {
      let chatHistory = [];
      chatHistory.push({ role: "user", parts: [{ text: prompt }] });
      const payload = { contents: chatHistory };
      const apiKey = "AIzaSyDU9V0yP3_xHWGjJLsFZe0m2n7djBF--NQ"; // Canvas will automatically provide this in runtime
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (
        result.candidates &&
        result.candidates.length > 0 &&
        result.candidates[0].content &&
        result.candidates[0].content.parts &&
        result.candidates[0].content.parts.length > 0
      ) {
        const text = result.candidates[0].content.parts[0].text;
        setSummarizedBio(text);
      } else {
        setSummaryError("Failed to generate summary. Please try again.");
        console.error(
          "Gemini API Error: Unexpected response structure",
          result
        );
      }
    } catch (error) {
      setSummaryError("An error occurred while connecting to the API.");
      console.error("Gemini API Network Error:", error);
    } finally {
      setIsSummarizing(false);
    }
  };

  const gradientBackgroundClass =
    theme === "dark"
      ? "bg-gradient-to-br from-gray-900 to-gray-800" // Black and dark cream gradient for dark mode
      : "bg-gradient-to-br from-white to-amber-50"; // White and light cream gradient for light mode

  return (
    <section
      id="about"
      className={`min-h-screen py-20 transition-colors duration-300 ${gradientBackgroundClass}`}
    >
      <div className="container mx-auto px-4">
        <motion.h2
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-4xl md:text-5xl font-bold text-center mb-12 text-gray dark:text-white"
        >
          About Me
        </motion.h2>

        <div className="flex flex-col md:flex-row items-center md:space-x-12 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="md:w-1/3 mb-8 md:mb-0"
          >
            <img
              src={personImage} // Updated to use a placeholder image
              alt=""
              className="rounded-full shadow-lg w-64 h-64 md:w-80 md:h-80 object-cover mx-auto border-4 border-purple-500 dark:border-pink-500"
            />
          </motion.div>
          <motion.div
            ref={textRef}
            style={{ opacity: textOpacity, y: textY }}
            className="md:w-2/3 text-lg text-gray dark:text-gray-300 leading-relaxed"
          >
            <p className="mb-4">
              Hello! I'm Ranjan, a detail-oriented web developer and MCA
              candidate specializing in Machine Learning and AI, with hands-on
              experience in building full-stack applications.
            </p>
            <p className="mb-4">
              I am proficient in modern front-end technologies like ReactJS,
              NextJS, and Framer Motion, and backend frameworks including
              Node.js and Flask. I have a demonstrated ability to integrate APIs
              and develop AI-powered tools, including emotion detection and
              chatbot systems.
            </p>
            <p>
              I have a strong foundation in Python, JavaScript, and database
              management with MongoDB and MySQL, and I'm always eager to learn
              and take on new challenges.
            </p>

            <motion.button
              onClick={handleSummarizeBio}
              disabled={isSummarizing}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mt-6 px-6 py-3 bg-blue-600 text-white font-semibold rounded-full shadow-md hover:bg-blue-700 transition-colors duration-300 flex items-center justify-center"
            >
              {isSummarizing ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Summarizing...
                </>
              ) : (
                <>
                  Summarize Bio <Sparkles className="ml-2" size={20} />
                </>
              )}
            </motion.button>

            <AnimatePresence>
              {summarizedBio && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.5 }}
                  className="mt-8 p-6 bg-purple-100 dark:bg-purple-900 rounded-lg shadow-inner border border-purple-300 dark:border-purple-700"
                >
                  <h4 className="text-xl font-bold text-purple-800 dark:text-purple-200 mb-3">
                    AI-Generated Summary:
                  </h4>
                  <p className="text-gray-800 dark:text-gray-200">
                    {summarizedBio}
                  </p>
                </motion.div>
              )}
              {summaryError && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.5 }}
                  className="mt-8 p-4 bg-red-100 dark:bg-red-900 rounded-lg shadow-inner border border-red-300 dark:border-red-700 text-red-800 dark:text-red-200"
                >
                  <p>{summaryError}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Education Timeline */}
          <div ref={educationRef}>
            <motion.h3
              style={{ opacity: educationOpacity, y: educationY }}
              className="text-3xl font-bold mb-8 text-gray dark:text-white text-center md:text-left"
            >
              Education
            </motion.h3>
            <div className="relative border-l-4 border-purple-500 dark:border-pink-500 pl-6 space-y-10">
              {educationData.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="relative p-6 bg-white dark:bg-gray-700 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 transform hover:-translate-y-1"
                >
                  <div className="absolute -left-3 top-0 mt-2 w-6 h-6 bg-purple-500 dark:bg-pink-500 rounded-full border-4 border-white dark:border-gray-800"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                    {item.year}
                  </p>
                  <h4 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
                    {item.title}
                  </h4>
                  <p className="text-md text-purple-600 dark:text-pink-400 mb-3">
                    {item.institution}
                  </p>
                  <p className="text-gray-700 dark:text-gray-300">
                    {item.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Experience Timeline */}
          <div ref={experienceRef}>
            <motion.h3
              style={{ opacity: experienceOpacity, y: experienceY }}
              className="text-3xl font-bold mb-8 text-gray dark:text-white text-center md:text-left"
            >
              Experience
            </motion.h3>
            <div className="relative border-l-4 border-blue-500 dark:border-green-500 pl-6 space-y-10">
              {experienceData.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="relative p-6 bg-white dark:bg-gray-700 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 transform hover:-translate-y-1"
                >
                  <div className="absolute -left-3 top-0 mt-2 w-6 h-6 bg-blue-500 dark:bg-green-500 rounded-full border-4 border-white dark:border-gray-800"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                    {item.year}
                  </p>
                  <h4 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
                    {item.title}
                  </h4>
                  <p className="text-md text-blue-600 dark:text-green-400 mb-3">
                    {item.company}
                  </p>
                  <p className="text-gray-700 dark:text-gray-300">
                    {item.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Projects Section */}
        <div ref={projectsRef} className="mt-20">
          <motion.h3
            style={{ opacity: projectsOpacity, y: projectsY }}
            className="text-3xl font-bold mb-8 text-gray dark:text-white text-center"
          >
            Projects
          </motion.h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projectsData.map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="p-6 bg-white dark:bg-gray-700 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 transform hover:-translate-y-1"
              >
                <h4 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
                  {project.title}{" "}
                  <a href={project.web} target="blank">
                    <span className="text-sm">(View)</span>
                  </a>
                </h4>
                <p className="text-sm text-purple-600 dark:text-pink-400 mb-3">
                  {project.technologies}
                </p>
                <p className="text-gray-700 dark:text-gray-300">
                  {project.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutPage;